import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useSEO } from "../utils/seo";
import {
  Phone, ArrowRight, CheckCircle, Upload, MapPin,
  FileText, Briefcase, Users, Sparkles
} from "lucide-react";
import axios from "axios";
import TalentCoverageMap from "../components/TalentCoverageMap";
import ClientTalentRequestForm from "../components/ClientTalentRequestForm";
import TalentMatchingVisual from "../components/TalentMatchingVisual";
import { getAttributionFields } from "../utils/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Visible role groups, used both for the primary-role <optgroup>s and
// for the "who's in the pool" display strip. Keeps the roles the same,
// just grouped so professionals see themselves quickly.
const ROLE_GROUPS = [
  {
    slug: "right",
    title: "Right-of-Way & Acquisition",
    roles: [
      "Right-of-way agent",
      "Pipeline Right-of-Way Agent",
      "Transmission Right-of-Way Agent",
      "Utility Right-of-Way Agent",
      "Acquisition Agent",
      "Landowner Negotiator",
    ],
  },
  {
    slug: "landman",
    title: "Landman & Leasing",
    roles: [
      "Landman",
      "Oil and Gas Landman",
      "Oil and Gas Lease Negotiator",
      "Renewable land agent",
      "Wind lease agent",
      "Solar lease agent",
      "Battery storage land agent",
    ],
  },
  {
    slug: "title",
    title: "Title, GIS & Documentation",
    roles: [
      "Title abstractor",
      "Title curative specialist",
      "GIS professional",
      "Document specialist",
      "Permitting specialist",
    ],
  },
  {
    slug: "project",
    title: "Project Management & Field",
    roles: [
      "Project manager",
      "Field coordinator",
      "Field Access / Survey Support Workflows",
    ],
  },
  {
    slug: "specialty",
    title: "Specialty",
    roles: [
      "Mining / Natural Resources Land Professional",
      "Other",
    ],
  },
];

const WORK_MODE_OPTIONS = ["Field", "Remote", "Hybrid", "Flexible"];

const ARRANGEMENT_OPTIONS = [
  "Contract / 1099",
  "Contract-to-Hire",
  "Direct Hire / W-2",
  "Consulting",
  "Open to Any Arrangement",
];

// Additional specialties (checkboxes, optional).
const SPECIALTIES = [
  "Right-of-Way Agent",
  "Landowner Negotiator",
  "Title / Abstracting",
  "Curative",
  "GIS / Mapping",
  "Project Management",
  "Field Access / Survey Support Workflows",
  "Solar Land",
  "Wind Land",
  "Battery Storage / BESS",
  "Pipeline / Midstream",
  "Transmission / Utility",
  "Mining / Natural Resources",
  "Other",
];

const AVAILABILITY = [
  "Available Now",
  "Available in 2 Weeks",
  "Available in 30 Days",
  "Available in 60 Days",
  "Available in 90 Days",
  "Currently Committed, Check Back Later",
  "Available in Approximately 6 Months",
  "Part-Time Only",
  "Project-Specific Only",
  "Not Available Right Now",
  "Not Interested Right Now, Keep Me in the Pool",
];

const YEARS_OPTIONS = [
  "Less than 1 Year", "1 to 3 Years", "3 to 5 Years", "5 to 10 Years",
  "10 to 15 Years", "15 to 20 Years", "20+ Years",
];

const TRAVEL_OPTIONS = ["Yes", "No", "Depends on project"];

const PREFERRED_PROJECT_TYPES = [
  "Pipeline / Midstream",
  "Transmission / Utility",
  "Oil and Gas Leasing",
  "Renewable Energy",
  "Solar",
  "Wind",
  "Battery Storage / BESS",
  "Renewable / Other",
  "Mining / Natural Resources",
  "Right-of-Way / Corridor",
  "Development / Greenfield",
  "Title / Curative",
  "GIS / Mapping Support",
  "Field Access / Survey Support Workflows",
  "PathScore Support",
  "AreaScore Support",
  "Other",
];

const TalentPool = () => {
  useSEO({
    title: "Onward Talent Network | Nationwide Land, ROW, Title, GIS, Renewable & PM Professionals",
    description:
      "Join Onward's Talent Network or request project-ready professionals. Nationwide pool of land, right-of-way, title, GIS, renewable, and project management talent. Local first, regional, then travel ready.",
    canonicalPath: "/talent-network",
    ogTitle: "Onward Talent Network | Nationwide Land Professionals",
    ogDescription:
      "Two-sided Talent Network. Professionals: join the pool. Clients: request one professional, several specialists, or a complete land team. Local first, regional, then travel ready.",
  });

  const formRef = useRef(null);
  const clientAnchorRef = useRef(null);
  const professionalAnchorRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const view = searchParams.get("view") ? (searchParams.get("view") === "client" ? "client" : "professional") : (location.hash === "#client-request" ? "client" : "professional");
  const setView = (v) => setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set("view", v); return p; }, { replace: false });

  const scrollToActiveForm = (targetView) => {
    const el = (targetView || view) === "client" ? clientAnchorRef.current : professionalAnchorRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 88; // account for fixed nav
    window.scrollTo({ top: y, behavior: "smooth" });
    // move focus to the heading for a11y
    setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (e) { /* noop */ } }, 350);
  };

  // On initial mount and whenever view or hash changes, scroll to the correct form.
  useEffect(() => {
    const hash = location.hash;
    if (hash === "#client-request" || hash === "#professional-application" || searchParams.get("view")) {
      // wait a tick so refs attach after conditional render
      const t = setTimeout(() => scrollToActiveForm(view), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [view, location.hash]);

  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    // Home base
    city: "", state: "",
    // Role + experience
    primary_role: "", years_of_experience: "",
    // Availability
    current_availability: "",
    // Travel + work areas
    willing_to_travel: "",
    willing_to_travel_nationally: "",
    states_willing_to_work: "",
    counties_regions: "",
    earliest_available_date: "",
    currently_on_assignment: "",
    // Background + preferences
    industries_worked: "",
    work_mode_preference: "",
    desired_arrangement: "",
    // Professional background
    linkedin_url: "",
    certifications: "",
    professional_summary: "",
    // Optional AI-assisted project-management experience (no LLM call, plain form fields)
    pm_ai_experience: "",
    pm_ai_tools_used: "",
    pm_ai_notes: "",
    // Free-text
    notes: "", consent: false,
  });
  const [specialties, setSpecialties] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const scrollToForm = () => scrollToActiveForm();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleFromList = (setter, list, value) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResume(null);
    if (!file) return;
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!validTypes.includes(file.type)) { setError("Please upload a PDF, DOC, or DOCX file."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("File must be under 10MB."); return; }
    setError(null);
    setResume(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resume) { setError("Please upload your resume."); return; }
    if (!formData.primary_role) { setError("Please select your primary role."); return; }
    if (!formData.consent) { setError("Please accept the consent statement."); return; }
    setLoading(true);
    setError(null);

    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => data.append(k, String(v)));
    // Backend contract: primary_specialty is required, send the selected primary role.
    data.append("primary_specialty", formData.primary_role);
    data.append("specialties_list", specialties.join(", "));
    data.append("preferred_project_types", projectTypes.join(", "));
    // Attribution capture (backward-compatible optional fields on backend)
    const attribution = getAttributionFields();
    Object.entries(attribution).forEach(([k, v]) => data.append(k, String(v || "")));
    data.append("source_url", window.location.href);
    data.append("resume", resume);

    try {
      await axios.post(`${API}/talent-network/submit`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again or email info@onwardland.com directly.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="min-h-screen flex items-center justify-center px-4 py-24 talent-pool">
        <div className="max-w-lg text-center">
          <div className="rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(200,16,46,0.15)" }}>
            <CheckCircle className="h-10 w-10" style={{ color: "#D93A54" }} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Profile Submitted</h1>
          <p className="text-slate-300 text-lg mb-2">
            Thank you for submitting your information to the Onward Talent Pool.
          </p>
          <p className="text-slate-400">
            Onward may contact you if a future project opportunity matches your home base, work area,
            specialty, experience, or availability.
          </p>
        </div>
      </section>
    );
  }

  return (
    <div className={`talent-pool talent-pool--${view}`} data-testid={`talent-view-${view}`}>
      {/* Active-view rail: colored top strip so the two views feel visibly different */}
      <div
        aria-hidden="true"
        style={{
          height: "4px",
          background: view === "client" ? "#D97423" : "#C8102E",
        }}
      />

      {/* Hero */}
      <section className="pt-20 pb-14 relative" data-testid="talent-hero-section">
        <div
          className="absolute inset-0"
          style={{
            background:
              view === "client"
                ? "linear-gradient(180deg, #0E0F14 0%, #17263B 55%, #0E0F14 100%)"
                : "linear-gradient(180deg, #0E0F14 0%, #0B1D34 55%, #0E0F14 100%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="talent-pool__eyebrow mb-4" data-testid="talent-hero-eyebrow">
            ONWARD TALENT NETWORK
          </p>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight"
                data-testid="talent-hero-heading"
              >
                The Right Land Professional, Closer to the Project.
              </h1>
              <p className="text-base sm:text-lg text-slate-300 mb-6 max-w-2xl" data-testid="talent-hero-subhead">
                Onward connects clients with project-ready land, right-of-way, title, GIS, renewable
                energy, and project-management professionals. We start with qualified professionals
                closest to the project, then expand by experience, travel preference, and availability.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4" data-testid="talent-hero-audience-controls">
                <button
                  type="button"
                  onClick={() => { if (view === "client") { scrollToActiveForm("client"); } else { setView("client"); } }}
                  aria-pressed={view === "client"}
                  aria-controls="client-request"
                  className="ols-btn-primary"
                  style={{ display: "inline-flex", justifyContent: "center" }}
                  data-testid="talent-hero-cta-client"
                >
                  Request Contract Land Help <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { if (view === "professional") { scrollToActiveForm("professional"); } else { setView("professional"); } }}
                  aria-pressed={view === "professional"}
                  aria-controls="professional-application"
                  className="ols-btn-talent"
                  style={{ display: "inline-flex", justifyContent: "center" }}
                  data-testid="talent-hero-cta-professional"
                >
                  Join the Talent Pool <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div data-testid="talent-hero-visual">
              <TalentMatchingVisual />
            </div>
          </div>
        </div>
      </section>

      {/* Two large choice cards removed: audience controls above the hero already own the state switch. */}

      {/* ACTIVE FORM SECTION: renders immediately below the hero so the client and professional actions land on the correct form without scrolling past the map. */}
      <section className="pt-10 pb-6" data-testid="talent-active-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {view === "client" ? (
            <div>
              <div
                ref={clientAnchorRef}
                id="client-request"
                tabIndex="-1"
                className="mb-6 outline-none scroll-mt-24"
                data-testid="client-request-anchor"
              >
                <p className="talent-pool__eyebrow mb-3" style={{ color: "#C8102E" }}>For Clients</p>
                <h2 className="text-3xl font-bold text-white mb-3" data-testid="client-request-heading">
                  Tell Us What the Project Requires
                </h2>
                <p className="text-slate-300 text-base leading-relaxed">
                  Share the project location, roles needed, timing, and staffing level. Onward reviews the
                  request and follows up. No commitment. No public listing.
                </p>
              </div>
              <ClientTalentRequestForm />
            </div>
          ) : (
            <div
              ref={professionalAnchorRef}
              id="professional-application"
              tabIndex="-1"
              className="outline-none scroll-mt-24"
              data-testid="professional-application-anchor"
            >
              <div className="text-center mb-10">
                <p className="talent-pool__eyebrow mb-3" style={{ color: "#e07a70" }}>For Professionals</p>
                <h2 className="text-3xl font-bold text-white mb-4" data-testid="professional-application-heading">
                  Submit Your Talent Profile
                </h2>
                <p className="text-slate-400">Required fields marked with *</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="talent-form" ref={formRef}>
                {/* Name */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="First Name *" name="first_name" required value={formData.first_name} onChange={handleChange} placeholder="First name" testId="talent-first-name" />
                  <Field label="Last Name *"  name="last_name"  required value={formData.last_name}  onChange={handleChange} placeholder="Last name"  testId="talent-last-name" />
                </div>
                {/* Contact */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email *" name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="you@email.com" testId="talent-email" />
                  <Field label="Phone *" name="phone" type="tel"   required value={formData.phone} onChange={handleChange} placeholder="(555) 000-0000" testId="talent-phone" />
                </div>

                {/* Home base */}
                <div className="talent-pool__section-heading"><SectionNum n={1} />Home Base</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Home City *"  name="city"  required value={formData.city}  onChange={handleChange} placeholder="City" testId="talent-city" />
                  <Field label="Home State *" name="state" required value={formData.state} onChange={handleChange} placeholder="TX"   testId="talent-state" />
                </div>
                <p className="text-slate-500 text-xs -mt-2">
                  Default local work radius is approximately 100 miles from your home base. You can expand
                  work areas below.
                </p>

                {/* Primary role + experience */}
                <div className="talent-pool__section-heading"><SectionNum n={2} />Role &amp; Experience</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <GroupedSelectField
                    label="Primary Role *"
                    name="primary_role"
                    required
                    value={formData.primary_role}
                    onChange={handleChange}
                    groups={ROLE_GROUPS}
                    testId="talent-primary-role"
                  />
                  <SelectField label="Years of Experience *" name="years_of_experience" required value={formData.years_of_experience} onChange={handleChange} options={YEARS_OPTIONS} testId="talent-years" />
                </div>
                <Field label="Industries Worked" name="industries_worked" value={formData.industries_worked} onChange={handleChange} placeholder="e.g. Pipeline, Transmission, Solar, Wind, Battery Storage" testId="talent-industries" />

                {/* Resume */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Resume Upload * (PDF, DOC, or DOCX, max 10MB)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    data-testid="talent-resume"
                    className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:font-semibold file:text-sm focus:outline-none"
                    style={{ colorScheme: "dark" }}
                  />
                  {resume && <p className="text-sm mt-1" style={{ color: "#e07a70" }}>{resume.name} selected</p>}
                </div>

                {/* Specialty checkboxes */}
                <div className="talent-pool__section-heading"><SectionNum n={3} />Additional Specialties</div>
                <p className="text-slate-500 text-xs -mt-2">Optional. Select any that apply beyond your primary role.</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {SPECIALTIES.map((s) => (
                    <label key={s} className="flex items-center gap-2 talent-pool__check">
                      <input
                        type="checkbox"
                        checked={specialties.includes(s)}
                        onChange={() => toggleFromList(setSpecialties, specialties, s)}
                        data-testid={`talent-spec-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      />
                      <span className="text-slate-200 text-sm">{s}</span>
                    </label>
                  ))}
                </div>

                {/* Availability */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectField label="Current Availability *" name="current_availability" required value={formData.current_availability} onChange={handleChange} options={AVAILABILITY} testId="talent-availability" />
                  <SelectField label="Remote, Field, or Hybrid Preference" name="work_mode_preference" value={formData.work_mode_preference} onChange={handleChange} options={WORK_MODE_OPTIONS} testId="talent-work-mode" />
                </div>
                <SelectField label="Desired Employment or Contract Arrangement" name="desired_arrangement" value={formData.desired_arrangement} onChange={handleChange} options={ARRANGEMENT_OPTIONS} testId="talent-arrangement" />

                {/* Work areas */}
                <div className="talent-pool__section-heading"><SectionNum n={4} />Work Areas &amp; Travel Regions</div>
                <p className="text-slate-500 text-xs -mt-2">Where you are willing to work beyond your home 100-mile radius.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectField label="Willingness to Travel *" name="willing_to_travel" required value={formData.willing_to_travel} onChange={handleChange} options={TRAVEL_OPTIONS} testId="talent-travel" />
                  <SelectField label="Willing to Travel Nationally?" name="willing_to_travel_nationally" value={formData.willing_to_travel_nationally} onChange={handleChange} options={["Yes", "No"]} />
                </div>
                <Field label="States Willing to Work In" name="states_willing_to_work" value={formData.states_willing_to_work} onChange={handleChange} placeholder="e.g. TX, OK, NM, LA" />
                <Field label="Counties / Regions Willing to Work In" name="counties_regions" value={formData.counties_regions} onChange={handleChange} placeholder="e.g. Tarrant County TX, Permian Basin, ERCOT South" />

                {/* Preferred project types */}
                <div className="talent-pool__section-heading"><SectionNum n={5} />Preferred Project Types</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {PREFERRED_PROJECT_TYPES.map((pt) => (
                    <label key={pt} className="flex items-center gap-2 talent-pool__check">
                      <input
                        type="checkbox"
                        checked={projectTypes.includes(pt)}
                        onChange={() => toggleFromList(setProjectTypes, projectTypes, pt)}
                      />
                      <span className="text-slate-200 text-sm">{pt}</span>
                    </label>
                  ))}
                </div>

                {/* Availability details */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Earliest Available Date" name="earliest_available_date" type="date" value={formData.earliest_available_date} onChange={handleChange} />
                  <SelectField label="Currently On Assignment?" name="currently_on_assignment" value={formData.currently_on_assignment} onChange={handleChange} options={["No", "Yes"]} />
                </div>

                {/* Professional background */}
                <div className="talent-pool__section-heading"><SectionNum n={6} />Professional Background</div>
                <Field label="LinkedIn Profile" name="linkedin_url" type="url" value={formData.linkedin_url} onChange={handleChange} placeholder="https://www.linkedin.com/in/yourname" testId="talent-linkedin" />
                <Field label="Certifications and Licenses" name="certifications" value={formData.certifications} onChange={handleChange} placeholder="e.g. RPL, SR/WA, Notary, PMP" testId="talent-certifications" />
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Professional Summary</label>
                  <textarea
                    name="professional_summary" rows={4} value={formData.professional_summary} onChange={handleChange}
                    data-testid="talent-summary"
                    className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none"
                    placeholder="A short overview of your experience, project history, and strengths."
                  />
                </div>

                {/* Optional AI-assisted PM experience, plain form fields, no LLM call, all optional */}
                <div className="talent-pool__section-heading"><SectionNum n={7} />AI-Assisted Project Management (Optional)</div>
                <p className="text-slate-400 text-[13px] leading-relaxed -mt-2" data-testid="pm-ai-explainer">
                  <Sparkles className="inline h-4 w-4 mr-1" style={{ color: "#D97423" }} />
                  Optional. If you&rsquo;ve used AI-assisted tools alongside project-management work,
                  such as schedule review, landowner communication drafting, GIS or parcel data cleanup,
                  or reporting, tell us. Onward is not scoring or screening candidates with AI; these fields
                  simply help us understand your working style.
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    AI-Assisted PM Experience
                  </label>
                  <select
                    name="pm_ai_experience"
                    value={formData.pm_ai_experience}
                    onChange={handleChange}
                    data-testid="talent-pm-ai-experience"
                    className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none"
                  >
                    <option value="">Select (optional)</option>
                    <option>None yet. Open to learning</option>
                    <option>Light. Used AI tools occasionally on project work</option>
                    <option>Regular. Use AI tools weekly to support PM work</option>
                    <option>Advanced. Build workflows with AI tools</option>
                    <option>Prefer not to say</option>
                  </select>
                </div>
                <Field
                  label="AI Tools You've Used (optional)"
                  name="pm_ai_tools_used"
                  value={formData.pm_ai_tools_used}
                  onChange={handleChange}
                  placeholder="e.g. ChatGPT, Claude, Copilot, Notion AI, Otter, custom internal tools"
                  testId="talent-pm-ai-tools"
                />
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    How you&rsquo;ve used AI on projects (optional)
                  </label>
                  <textarea
                    name="pm_ai_notes"
                    rows={3}
                    value={formData.pm_ai_notes}
                    onChange={handleChange}
                    data-testid="talent-pm-ai-notes"
                    className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none"
                    placeholder="A sentence or two. Skip if it doesn't apply."
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Notes / Comments</label>
                  <textarea
                    name="notes" rows={3} value={formData.notes} onChange={handleChange}
                    className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none"
                    placeholder="Anything else you want Onward to know."
                  />
                </div>

                {/* Map coming soon note */}
                <div className="rounded-lg p-4 border" style={{ borderColor: "rgba(200,16,46,0.35)", background: "rgba(200,16,46,0.06)" }}>
                  <p className="text-[13px] leading-relaxed text-slate-300">
                    <strong style={{ color: "#e07a70" }}>Map-based work area selection is coming soon.</strong> Future
                    map feature will let you drop a home-base pin, view an approximate 100-mile radius, select
                    additional states or counties, and tag preferred travel zones. Until then, use the fields
                    above to describe your work areas.
                  </p>
                </div>

                {/* Consent */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox" name="consent" checked={formData.consent} onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 focus:ring-2"
                    style={{ accentColor: "#D93A54" }}
                    data-testid="talent-consent"
                  />
                  <label className="text-slate-400 text-sm leading-relaxed">
                    By joining the Talent Pool, I authorize Onward Land Solutions to store my resume and profile
                    information for project staffing opportunities and to contact me when a matching opportunity
                    becomes available. Onward may present my information to clients or project partners only for
                    relevant staffing opportunities. <a href="/talent-network/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: "#e07a70" }} data-testid="talent-privacy-link">Privacy Notice</a>
                  </label>
                </div>

                {error && <p className="text-red-400 text-sm" data-testid="talent-error">{error}</p>}

                <p className="text-slate-400 text-[13px] leading-relaxed" data-testid="talent-disclaimer">
                  Submission to the Onward Talent Pool does not guarantee placement or employment.
                  Information may be retained for consideration on future projects.
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  className="ols-btn-talent w-full justify-center text-lg py-4"
                  data-testid="talent-submit"
                >
                  {loading ? "Submitting..." : (<><Upload className="mr-2 h-5 w-5" /> Submit Talent Profile</>)}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* U.S. talent coverage map */}
      <section className="py-16" data-testid="coverage-map-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">Operating Utility-Scale Renewable Technology Mix</h2>
            <p className="text-slate-300 max-w-3xl mx-auto">
              This map shows the operating utility-scale wind, solar, and battery-storage technology
              mix reported by state. Onward supports renewable projects nationwide while also providing
              pipeline, transmission, oil and gas, right-of-way, title, GIS, staffing, and land-project
              services throughout the lower 48 states.
            </p>
          </div>
          <TalentCoverageMap />
          {/* NATIONWIDE CAPABILITIES, beyond renewables */}
          <div className="mt-14" data-testid="national-service-message">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-4">
                Nationwide Land, Right-of-Way and Infrastructure Support
              </h2>
              <p className="text-slate-300 max-w-4xl mx-auto">
                Onward provides individual professionals, supplemental staffing, complete project teams,
                and managed land-project support throughout the lower 48 states. Our capabilities extend
                beyond renewable energy to pipeline, midstream, electric transmission, utilities, oil and
                gas, mining, and other land-intensive infrastructure projects.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="capability-groups">
              {[
                { h: "Right-of-Way and Acquisition", items: ["Pipeline right-of-way acquisition","Transmission and utility right-of-way","Easement negotiations","Landowner outreach","Acquisition agents","Condemnation support","Access agreements","Damage settlements","Field coordination","Project reporting"] },
                { h: "Leasing and Site Control", items: ["Oil and gas leasing","Wind lease negotiations","Solar lease negotiations","Battery storage and BESS agreements","Option agreements","Surface-use agreements","Access and transmission easements","Lease amendments and extensions","Site-control programs","Landowner relationship management"] },
                { h: "Title, GIS and Project Controls", items: ["Ownership research","Title research and abstracting","Title curative","Independent title support","GIS mapping","Parcel and ownership tracking","Document control","Project dashboards and reporting","Survey coordination","Survey support workflows","Permitting support","Field-team coordination"] },
                { h: "Project Intelligence and Professional Staffing", items: ["PathScore corridor intelligence","AreaScore area and site intelligence","Contract landmen","Right-of-way agents","Renewable land agents","Title professionals","GIS professionals","Project managers","Field coordinators","Short-term staffing","Long-term staffing","Complete project teams","Managed land programs"] },
              ].map(({ h, items }) => (
                <div
                  key={h}
                  className="rounded-lg border p-6"
                  style={{ borderColor: "rgba(244,241,236,0.14)", background: "rgba(255,255,255,0.02)" }}
                  data-testid={`capability-group-${h.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}
                >
                  <h3 className="text-[16px] font-bold text-white mb-4">{h}</h3>
                  <ul className="space-y-1.5">
                    {items.map((it) => (
                      <li key={it} className="text-[13px] flex items-start gap-2 text-slate-300">
                        <span className="mt-[6px] w-[5px] h-[5px] flex-shrink-0" style={{ background: "#D97423", transform: "rotate(45deg)" }} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="text-slate-400 text-[13px] leading-relaxed max-w-4xl mx-auto text-center mt-6">
              PathScore helps project teams evaluate and compare linear routes, corridors, and
              rights-of-way. AreaScore helps project teams screen and compare larger development areas,
              regions, and site-search territories.
            </p>

            <div
              className="mt-10 rounded-lg border p-8 text-center"
              style={{ borderColor: "rgba(244,241,236,0.14)", background: "rgba(191,87,0,0.06)" }}
            >
              <p className="text-xl sm:text-2xl font-bold text-white mb-4 max-w-3xl mx-auto" data-testid="positioning-statement">
                From one experienced land professional to a complete managed land program, Onward can
                scale around the project.
              </p>
              <p className="text-sm mt-4 font-medium" style={{ color: "#D97423" }} data-testid="lower48-service-line">
                Pipeline, transmission, oil and gas leasing, right-of-way, title, GIS, and land-project
                support are available throughout the lower 48 states.
              </p>
            </div>
          </div>
        </div>
      </section>

      {view === "client" && (
        <>
          <section className="py-14" style={{ background: "rgba(217,116,35,0.06)" }} data-testid="client-story">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <p className="talent-pool__eyebrow mb-3" style={{ color: "#C8102E" }}>For Clients</p>
              <h2 className="text-3xl font-bold text-white mb-5" data-testid="client-story-heading">
                Need the Right Professional Near Your Project?
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-6" data-testid="client-story-copy">
                Tell Onward where the project is located, what role you need, how many professionals are
                required, and when the work begins. We search qualified local professionals first,
                expand into the surrounding region, and then consider experienced travel-ready talent
                when needed.
              </p>
              <ol className="grid sm:grid-cols-3 gap-3" data-testid="client-story-steps">
                {[
                  { n: "1", title: "Local First", body: "Qualified professionals nearest the project." },
                  { n: "2", title: "Expand Regionally", body: "Adjacent counties, states, and regions." },
                  { n: "3", title: "Confirm Availability", body: "We verify interest and timing before presenting anyone." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="rounded-lg p-4 border"
                    style={{ borderColor: "rgba(217,116,35,0.35)", background: "rgba(0,0,0,0.30)" }}
                    data-testid={`client-step-${s.n}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        aria-hidden="true"
                        style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: "26px", height: "26px", borderRadius: "999px",
                          background: "#D97423", color: "#0E0F14", fontWeight: 800, fontSize: "13px",
                        }}
                      >
                        {s.n}
                      </span>
                      <strong className="text-white text-[15px]">{s.title}</strong>
                    </div>
                    <p className="text-slate-300 text-[13.5px] leading-relaxed">{s.body}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </>
      )}

      {view === "professional" && (
      <>
      {/* Who's in the pool (visible role groups) */}
      <section className="py-14" style={{ background: "rgba(0,0,0,0.15)" }} data-testid="role-groups-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-8">
            <p className="talent-pool__eyebrow mb-3" style={{ color: "#e07a70" }}>Who&rsquo;s in the pool</p>
            <h2 className="text-3xl font-bold text-white mb-4">
              Land, energy, and infrastructure professionals, grouped so you see yourself fast.
            </h2>
            <p className="text-slate-300 text-[15px] leading-relaxed">
              Onward maintains a nationwide Talent Network across right-of-way and acquisition, landman
              and leasing, title and GIS, project management and field, and specialty land roles.
              Your primary role goes on your profile. Additional specialties tell us where else you can
              contribute on a project.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="role-groups-grid">
            {ROLE_GROUPS.map((group) => (
              <div
                key={group.title}
                className="rounded-lg border p-5"
                style={{ borderColor: "rgba(244,241,236,0.14)", background: "rgba(255,255,255,0.02)" }}
                data-testid={`role-group-${group.slug}`}
              >
                <h3 className="text-[15px] font-bold text-white mb-3">{group.title}</h3>
                <ul className="space-y-1.5">
                  {group.roles.map((r) => (
                    <li key={r} className="text-[13px] flex items-start gap-2 text-slate-300">
                      <span className="mt-[6px] w-[5px] h-[5px] flex-shrink-0" style={{ background: "#D97423", transform: "rotate(45deg)" }} />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story copy (professional) */}
      <section className="py-16" style={{ background: "rgba(200,16,46,0.06)" }} data-testid="professional-story">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="talent-pool__eyebrow mb-3" style={{ color: "#e07a70" }}>For Professionals</p>
          <h2 className="text-3xl font-bold text-white mb-6" data-testid="professional-story-heading">
            Join Now, Even If You Are Currently Working.
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed" data-testid="professional-story-copy">
            You do not need to be unemployed or immediately available to join the Onward Talent Pool.
            Availability changes, assignments end, and the right project may align later. Tell us
            your experience, home base, preferred work areas, travel willingness, and availability so
            we can contact you when a relevant opportunity develops.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16" style={{ background: "rgba(0,0,0,0.15)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-6">How the Talent Pool Works</h2>
          <p className="text-slate-300 text-lg mb-6">
            Onward maintains a national pool of qualified land professionals and project specialists. When you
            join the Talent Pool, we collect your resume, specialties, home base, preferred work areas,
            travel willingness, and availability.
          </p>
          <p className="text-slate-300 text-lg mb-6">
            We use your home area as a starting point and identify opportunities within an approximate
            <strong className="text-white"> 100-mile radius</strong>, then expand based on the states,
            counties, regions, or project types you are willing to travel for.
          </p>
          <p className="text-slate-300 text-lg mb-6">
            When reviewing a client project, Onward considers location, specialty, experience and availability.
            Our team confirms interest and availability before
            presenting you for the opportunity.
          </p>
          <p className="text-slate-300 text-lg">
            This helps Onward quickly assemble local or regional teams for right-of-way projects, renewable
            projects, pipeline and transmission work, title support, GIS support, field access, and project
            management assignments.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", icon: Upload, title: "Upload your resume" },
              { step: "2", icon: Briefcase, title: "Tell us your specialty & availability" },
              { step: "3", icon: MapPin, title: "Set your home base & work areas" },
              { step: "4", icon: Phone, title: "We contact you when a project fits" },
            ].map(({ step, icon: Icon, title }) => (
              <div key={step} className="text-center">
                <div className="rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(200,16,46,0.20)" }}>
                  <Icon className="h-6 w-6" style={{ color: "#e07a70" }} />
                </div>
                <div className="font-bold text-sm mb-2" style={{ color: "#e07a70" }}>Step {step}</div>
                <p className="text-white font-semibold text-sm">{title}</p>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-center text-sm mt-10">
            Submitting your information does not guarantee employment, assignment, placement, or project work.
          </p>
        </div>
      </section>

      {/* Professional form moved above the coverage map. Supporting content remains here. */}

      {/* Footer CTA */}
      <section className="py-16" style={{ background: "rgba(0,0,0,0.12)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="h-8 w-8 mx-auto mb-4" style={{ color: "#e07a70" }} />
          <h2 className="text-2xl font-bold text-white mb-4">Ready to join the Talent Pool?</h2>
          <p className="text-slate-300 mb-8">
            Upload your resume and keep your availability current so Onward can consider you for future
            project opportunities by home base, work area, specialty, and availability.
          </p>
          <button onClick={scrollToForm} className="ols-btn-talent">
            <FileText className="mr-2 h-5 w-5" />
            Upload Your Resume
          </button>
        </div>
      </section>
      </>
      )}
    </div>
  );
};

const Field = ({ label, name, type = "text", required, value, onChange, placeholder, testId }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>
    <input
      name={name} type={type} required={required} value={value} onChange={onChange}
      placeholder={placeholder} data-testid={testId}
      className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none"
    />
  </div>
);

const SelectField = ({ label, name, required, value, onChange, options, testId }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>
    <select
      name={name} required={required} value={value} onChange={onChange}
      data-testid={testId}
      className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none"
    >
      <option value="">Select</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const GroupedSelectField = ({ label, name, required, value, onChange, groups, testId }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>
    <select
      name={name} required={required} value={value} onChange={onChange}
      data-testid={testId}
      className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none"
    >
      <option value="">Select your primary role</option>
      {groups.map((g) => (
        <optgroup key={g.title} label={g.title}>
          {g.roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </optgroup>
      ))}
    </select>
  </div>
);

const SectionNum = ({ n }) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "22px",
      height: "22px",
      padding: "0 6px",
      marginRight: "10px",
      borderRadius: "999px",
      background: "rgba(224,122,112,0.15)",
      border: "1px solid rgba(224,122,112,0.35)",
      color: "#e07a70",
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.5px",
      verticalAlign: "middle",
    }}
  >
    {String(n).padStart(2, "0")}
  </span>
);

export default TalentPool;
